import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { t } from '@/i18n';
import type { MonthlyRow, TopEmployeeRow } from './hrStatsAggregation';

/**
 * The three absence buckets keep FIXED categorical slots across both charts
 * (urlop → chart-1, L4 → chart-2, pozostałe → chart-3) — filtering or
 * ranking never repaints a series.
 */
function bucketsConfig(): ChartConfig {
  return {
    vacation: { label: t('hr.stats.chartVacation'), color: 'var(--chart-1)' },
    sick: { label: t('hr.stats.chartSick'), color: 'var(--chart-2)' },
    other: { label: t('hr.stats.chartOther'), color: 'var(--chart-3)' },
  };
}

const MONTH_LABELS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

/** Approved absence days per month, stacked by bucket. */
export function AbsenceMonthlyChart({ rows, year }: { rows: MonthlyRow[]; year: number }) {
  const data = rows.map((r) => ({ ...r, label: MONTH_LABELS[r.month - 1] }));
  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-semibold">
          {t('hr.stats.monthlyChartTitle', { year })}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ChartContainer config={bucketsConfig()} className="h-56 w-full">
          <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid vertical={false} strokeOpacity={0.4} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis tickLine={false} axisLine={false} fontSize={11} allowDecimals={false} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar dataKey="vacation" stackId="a" fill="var(--color-vacation)" />
            <Bar dataKey="sick" stackId="a" fill="var(--color-sick)" />
            <Bar dataKey="other" stackId="a" fill="var(--color-other)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}

/** Top employees by approved absence days, horizontal, stacked by bucket. */
export function AbsenceTopChart({ rows, year }: { rows: TopEmployeeRow[]; year: number }) {
  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-semibold">
          {t('hr.stats.topChartTitle', { year })}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {rows.length === 0 ? (
          <p className="text-muted-foreground py-12 text-center text-sm">{t('hr.stats.empty')}</p>
        ) : (
          <ChartContainer config={bucketsConfig()} className="w-full" style={{ height: Math.max(140, rows.length * 28 + 60) }}>
            <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 40 }}>
              <CartesianGrid horizontal={false} strokeOpacity={0.4} />
              <XAxis type="number" tickLine={false} axisLine={false} fontSize={11} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="employeeName"
                tickLine={false}
                axisLine={false}
                fontSize={11}
                width={130}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              <Bar dataKey="vacation" stackId="a" fill="var(--color-vacation)" />
              <Bar dataKey="sick" stackId="a" fill="var(--color-sick)" />
              <Bar dataKey="other" stackId="a" fill="var(--color-other)" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
